import React from "react";
import { motion } from "framer-motion";
import { AlertCircleIcon, BarChart3Icon, FileTextIcon, BoxIcon } from "lucide-react";
import { useLanguage } from "../LanguageContext";
import { trustChallengeCopy as c, trustEvidence, trustedJourney, unverifiedJourney, Bi, EvidenceIcon } from "../../data/trustChallenge";
import { TrustJourneyPanel } from "./TrustJourney";
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const EVIDENCE_ICONS: Record<EvidenceIcon, BoxIcon> = {
  alert: AlertCircleIcon,
  doc: FileTextIcon,
  chart: BarChart3Icon
};
export function TrustChallenge() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative bg-white border-t border-[#EEF0F4] px-6 py-16 lg:py-20" aria-labelledby="trust-challenge-title" data-id="element-6844" data-mp="1yew91oi8wipa">
      <div className="max-w-[1400px] mx-auto" data-id="element-6845" data-mp="aqvnw7sot2p3">
        <div className="grid grid-cols-1 xl:grid-cols-[45fr_55fr] gap-10 xl:gap-10 items-start" data-id="element-6846" data-mp="fqqc23l2ve6g">
          <motion.div {...reveal} data-id="element-6847" data-mp="1qyslnbsl5l2">
            <p className="font-sans text-[12.5px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5]" data-id="element-6848" data-mp="1rql2pa3en74">{pick(c.eyebrow)}</p>
            <h2 id="trust-challenge-title" className="mt-4 font-serif text-[2.2rem] sm:text-[2.9rem] lg:text-[3.3rem] 2xl:text-[3.6rem] leading-[1.03] tracking-tight" data-id="element-6849" data-mp="5t2el5w37skl">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6850" data-mp="yfwqvfir3lmz">{pick(l.text)}</span>)}
            </h2>
            <p className="mt-5 max-w-[560px] font-sans text-[16px] md:text-[18px] text-[#5A6178] leading-relaxed" data-id="element-6851" data-mp="oreq2yah1ejc">{pick(c.body)}</p>
          </motion.div>

          <motion.ul {...reveal} className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-6852" data-mp="1cffvhyccseuj">
            {trustEvidence.map(e => {
            const Icon = EVIDENCE_ICONS[e.icon];
            return <li key={e.value.fr} className="rounded-2xl border border-[#EEEFF4] bg-white p-5 flex flex-col shadow-[0_8px_24px_-20px_rgba(18,26,58,0.3)]" data-id="element-6853" data-mp="1r2obdcglx8cu">
                  <span className="w-14 h-14 rounded-full bg-[#FDECEC] flex items-center justify-center" data-id="element-6854" data-mp="1i318em88buho">
                    <Icon className="w-6 h-6 text-[#E5484D]" strokeWidth={1.7} aria-hidden="true" data-id="element-6855" data-mp="1d3wy1s9kjqpr" />
                  </span>
                  <p className="mt-5 font-sans text-[2.6rem] lg:text-[2.9rem] font-semibold leading-none tracking-tight text-[#EB5A5E] whitespace-nowrap" data-id="element-6856" data-mp="1853k5yqddxp2">
                    {pick(e.value)}
                  </p>
                  <p className="mt-3 font-sans text-[14.5px] text-[#3A4260] leading-snug" data-id="element-6857" data-mp="zdl3zntfbhvz">{pick(e.text)}</p>
                  <p className="mt-auto pt-4" data-id="element-6858" data-mp="ueqbfu5nmeyo">
                    <span className="block border-t border-[#EEEFF4] pt-3 font-sans text-[12.5px] text-[#6B7288]" data-id="element-6859" data-mp="peceb75lcpff">{e.source}</span>
                  </p>
                </li>;
          })}
          </motion.ul>
        </div>

        <motion.div {...reveal} className="mt-10 grid grid-cols-1 2xl:grid-cols-2 gap-4" data-id="element-6860" data-mp="abu95b43ecrp">
          <TrustJourneyPanel variant="problem" title={pick(unverifiedJourney.title)} sub={pick(unverifiedJourney.sub)} steps={unverifiedJourney.steps} result={pick(unverifiedJourney.result)} pick={pick} data-id="element-6861" data-mp="fa5i7jmkcj8v" />
          <TrustJourneyPanel variant="trusted" title={pick(trustedJourney.title)} sub={pick(trustedJourney.sub)} steps={trustedJourney.steps} result={pick(trustedJourney.result)} pick={pick} data-id="element-6862" data-mp="p94st7qw2oyi" />
        </motion.div>
      </div>
    </section>;
}